<template>
  <b-overlay :show="isLoading || selectAllTradiesBusy">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <div class="ptl-toolbar">
      <div class="ptl-toolbar__actions">
        <b-button
          size="sm"
          variant="outline-secondary"
          class="ptl-action-btn"
          :disabled="selectAllTradiesBusy"
          @click="selectAllTradies"
        >
          <b-icon-check2-all class="mr-1" />
          Select all
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="ptl-action-btn"
          @click="clearSelected"
        >
          <b-icon-x-circle class="mr-1" />
          Clear
        </b-button>
      </div>

      <div class="ptl-toolbar__search">
        <b class="ptl-search-label">Search:</b>
        <b-form-input
          v-model="search"
          placeholder="Type to search..."
          debounce="500"
          class="ptl-search-input"
        />
      </div>
    </div>

    <b-table
      id="tradies-table"
      class="ptl-table"
      responsive
      hover
      show-empty
      ref="tradiesTable"
      :fields="fields"
      :items="tradies"
      selectable
      @row-selected="onTradiesSelected"
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <digger-loader />
        </div>
      </template>

      <template #head(selected)>
        <span
          :style="{
            cursor: selectAllTradiesBusy ? 'wait' : 'pointer',
            'user-select': 'none',
            opacity: selectAllTradiesBusy ? 0.5 : 1,
          }"
          @click="!selectAllTradiesBusy && selectAllTradies()"
        >
          Select All
        </span>
      </template>

      <template #cell(selected)="{ index, rowSelected }">
        <b-form-checkbox
          v-model="rowSelected"
          inline
          @change="onRowChecked(index, !rowSelected)"
        />
      </template>
    </b-table>

    <b-pagination
      v-if="rows > perPage"
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :current-page="currentPage"
      aria-controls="tradies-table"
      align="center"
    />

    <p
      v-if="tradies"
      align="center"
      class="ptl-count-note small text-muted mb-0"
    >
      Showing {{ tradies.length }} out of {{ rows }} tradies
    </p>
  </b-overlay>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import User from '@/models/User'

export default {
  async mounted() {
    window.addEventListener('resize', this.onResize)
    await this.fetchTradies(this.currentPage, this.perPage, this.search)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },

  props: {
    projectId: Number,
  },

  data() {
    return {
      isLoading: false,
      selectAllTradiesBusy: false,
      perPage: 5,
      currentPage: 1,
      search: '',
      // Tracks viewport width so the picker table can shed columns on phones.
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
      selectedTradieIds: [],
      prevSelectedIds: [],
    }
  },

  computed: {
    ...mapGetters('user', {
      tradies: 'users',
      tradiesMeta: 'usersMeta',
    }),

    /**
     * Picker columns. Progressively reveals columns as the modal widens so it
     * stays compact and fits its container at 320px:
     *   - xs (<576):  Select, Name
     *   - sm (>=576): + Email
     *   - md (>=768): + Phone, + Trade
     */
    fields() {
      const short = this.windowWidth < 576
      return [
        {
          key: 'selected',
          label: 'Select All',
          thClass: 'text-nowrap text-center',
          tdClass: 'text-center align-middle',
        },
        {
          key: 'full_name',
          label: short ? 'Name' : 'Full Name',
          // Centered on phones (Select + Name only), left-aligned from sm up.
          thClass: 'text-nowrap text-center text-sm-left',
          tdClass: 'text-center text-sm-left align-middle',
        },
        {
          key: 'email',
          label: 'Email',
          thClass: 'text-nowrap d-none d-sm-table-cell',
          tdClass: 'd-none d-sm-table-cell',
        },
        {
          key: 'phone_number',
          label: 'Phone',
          thClass: 'text-nowrap d-none d-md-table-cell',
          tdClass: 'd-none d-md-table-cell',
        },
        {
          key: 'trade',
          label: 'Trade',
          thClass: 'text-nowrap d-none d-md-table-cell',
          tdClass: 'd-none d-md-table-cell',
        },
      ]
    },

    rows() {
      return this.tradiesMeta ? this.tradiesMeta.total : 0
    },
  },

  watch: {
    currentPage: async function (newCurrentPage) {
      await this.fetchTradies(newCurrentPage, this.perPage, this.search)
    },

    search: async function (newSearch) {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        await this.fetchTradies(1, this.perPage, newSearch)
      }
    },
  },

  methods: {
    ...mapActions('user', {
      getTradies: 'getUsers',
    }),

    onResize() {
      this.windowWidth = window.innerWidth
    },

    fetchingTradies(status) {
      this.isLoading = status
    },

    async fetchTradies(page, limit, search) {
      this.fetchingTradies(true)

      await this.getTradies({
        page,
        limit,
        search,
        userType: 'tradie',
        projectId: this.projectId,
      }).finally(() => {
        this.fetchingTradies(false)

        this.selectedTradieIds.forEach((x) => {
          this.tradies.forEach((tradie, index) => {
            if (tradie.id === x) this.$refs.tradiesTable.selectRow(index)
          })
        })
      })
    },

    onTradiesSelected(tradies) {
      if (!this.isLoading) {
        const newSelectedIds = tradies.map((tradie) => tradie.id)
        const prevSelectedIds = Object.values(this.prevSelectedIds)

        const tradieId = prevSelectedIds
          .filter((x) => !newSelectedIds.includes(x))
          .concat(newSelectedIds.filter((x) => !prevSelectedIds.includes(x)))[0]

        // IF Adding
        if (newSelectedIds.length > prevSelectedIds.length) {
          if (!this.selectedTradieIds.includes(tradieId))
            this.selectedTradieIds.push(tradieId)
        }
        // ELSE (Removing)
        else {
          this.selectedTradieIds = this.selectedTradieIds.filter(
            (x) => x !== tradieId,
          )
        }

        this.prevSelectedIds = newSelectedIds

        this.$emit('tradieSelected', this.selectedTradieIds)
      } else {
        // Page Changing
        this.prevSelectedIds = []
      }
    },

    onRowChecked(index, selected) {
      if (!selected) this.$refs.tradiesTable.selectRow(index)
      else this.$refs.tradiesTable.unselectRow(index)
    },

    idsFromUsersResponse(res) {
      const users = Array.isArray(res) ? res : res && res.data ? res.data : []
      const ids = []
      users.forEach((u) => {
        if (u && u.id != null) ids.push(u.id)
      })
      return ids
    },

    async fetchAllTradieIdsForCurrentFilter() {
      const meta = this.tradiesMeta || {}
      const total = meta.total != null ? meta.total : this.rows
      if (!total) return []

      const perPage = this.perPage
      const lastPage =
        meta.last_page != null
          ? meta.last_page
          : Math.max(1, Math.ceil(total / perPage))

      const tasks = []
      for (let p = 1; p <= lastPage; p++) {
        const query = User.page(p).limit(perPage)
        query.where('search', this.search || null)
        query.where('user_type', 'tradie')
        query.where('project_id', this.projectId)
        tasks.push(query.get())
      }
      const results = await Promise.all(tasks)
      const ids = []
      results.forEach((res) => {
        ids.push(...this.idsFromUsersResponse(res))
      })
      return [...new Set(ids)]
    },

    async selectAllTradies() {
      if (this.selectAllTradiesBusy || this.isLoading) return

      this.selectAllTradiesBusy = true
      try {
        const allIds = await this.fetchAllTradieIdsForCurrentFilter()
        if (!allIds.length) {
          this.$emit('tradieSelected', this.selectedTradieIds)
          return
        }

        const allInSelection = allIds.every((id) =>
          this.selectedTradieIds.includes(id),
        )

        if (allInSelection) {
          this.selectedTradieIds = this.selectedTradieIds.filter(
            (id) => !allIds.includes(id),
          )
          this.tradies.forEach((tradie, index) => {
            this.$refs.tradiesTable.unselectRow(index)
          })
        } else {
          allIds.forEach((id) => {
            if (!this.selectedTradieIds.includes(id)) {
              this.selectedTradieIds.push(id)
            }
          })
          this.tradies.forEach((tradie, index) => {
            if (this.selectedTradieIds.includes(tradie.id)) {
              this.$refs.tradiesTable.selectRow(index)
            } else {
              this.$refs.tradiesTable.unselectRow(index)
            }
          })
        }

        this.prevSelectedIds = this.tradies
          .filter((t) => this.selectedTradieIds.includes(t.id))
          .map((t) => t.id)

        this.$emit('tradieSelected', this.selectedTradieIds)
      } catch (e) {
        console.error(e)
      } finally {
        this.selectAllTradiesBusy = false
      }
    },

    clearSelected() {
      this.$refs.tradiesTable.clearSelected()
      this.selectedTradieIds = []
      this.prevSelectedIds = []
      this.$emit('tradieSelected', this.selectedTradieIds)
    },
  },
}
</script>

<style lang="scss" scoped>
/* Toolbar: Select all / Clear actions on the left, search on the right.
   Wraps to a tidy stacked layout on phones. */
.ptl-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

/* The Select column checkbox has no label text, but bootstrap-vue still
   reserves the label's padding/margin — which shifts the box off-centre in
   the centred cell. Trim that reserved space so the box sits truly centred. */
/deep/ #tradies-table td .custom-control.custom-checkbox {
  padding-left: 1rem;
  margin-right: 0;
}
/deep/
  #tradies-table
  td
  .custom-control.custom-checkbox
  .custom-control-label::before,
/deep/
  #tradies-table
  td
  .custom-control.custom-checkbox
  .custom-control-label::after {
  left: -1rem;
}

.ptl-toolbar__actions {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
}

.ptl-action-btn {
  font-weight: 600;
  white-space: nowrap;
}

.ptl-toolbar__search {
  flex: 1 1 220px;
  min-width: 180px;
}

.ptl-search-label {
  display: block;
  font-size: 0.85rem;
  margin-bottom: 0.15rem;
}

@media (max-width: 767.98px) {
  /* Below the laptop layout: actions centred as a compact pair on their own
     line, full-width search below. Covers phones and narrow/tablet windows. */
  .ptl-toolbar {
    justify-content: center;
  }
  .ptl-toolbar__actions {
    flex: 1 1 100%;
    justify-content: center;
  }
  .ptl-action-btn {
    flex: 0 0 auto;
    min-width: 0;
  }
  .ptl-toolbar__search {
    flex: 1 1 100%;
    min-width: 0;
  }
  .ptl-search-input {
    font-size: 0.85rem;
  }

  /* Compact table: only Select + Name show here, so keep it snug. Matches
     .tradies-table's own mobile sizing on the Project Details page behind
     this modal. */
  /deep/ .ptl-table {
    font-size: 0.78rem;

    th,
    td {
      padding: 0.4rem 0.35rem;
    }
  }

  /* Matches the pagination/count-note sizing used across the Project
     Details page's own tables (ViewPage.vue's .tradies-pagination and
     .tradie-count) so this modal reads consistently with the rest of the
     app instead of its own slightly-off one-off values. */
  /deep/ .pagination {
    font-size: 0.85rem;
  }
  /deep/ .pagination .page-link {
    padding: 0.25rem 0.5rem;
  }

  .ptl-count-note {
    font-size: 0.8rem;
  }
}
</style>
